import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { AalimLogoCompact } from './AalimLogo';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Moon, Sun, User, LogOut, Settings, Crown, Menu, X, Shield } from 'lucide-react';
import { useState } from 'react';

export const Navbar = () => {
    const { user, logout, isPremium, isAdmin } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center" data-testid="nav-logo">
                        <AalimLogoCompact size={52} />
                        <span className="font-heading text-2xl font-semibold text-foreground -ml-2">aalim<span className="text-secondary">.ai</span></span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-6">
                        {user ? (
                            <>
                                <Link 
                                    to="/chat" 
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                    data-testid="nav-chat"
                                >
                                    Chat
                                </Link>
                                {isPremium && (
                                    <Link 
                                        to="/ustadh" 
                                        className="text-secondary hover:text-secondary/80 transition-colors flex items-center gap-1"
                                        data-testid="nav-ustadh"
                                    >
                                        <Crown className="w-4 h-4" />
                                        Ustadh Mode
                                    </Link>
                                )}
                                <Link 
                                    to="/subscription" 
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                    data-testid="nav-subscription"
                                >
                                    Subscription
                                </Link>
                                {isAdmin && (
                                    <Link 
                                        to="/admin" 
                                        className="text-secondary hover:text-secondary/80 transition-colors flex items-center gap-1"
                                        data-testid="nav-admin"
                                    >
                                        <Shield className="w-4 h-4" />
                                        Admin
                                    </Link>
                                )}
                            </>
                        ) : (
                            <>
                                <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">
                                    Features
                                </a>
                                <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                                    Pricing
                                </a>
                            </>
                        )}
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex items-center gap-3">
                        {/* Theme Toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="rounded-full"
                            data-testid="theme-toggle"
                        >
                            {theme === 'dark' ? (
                                <Sun className="h-5 w-5" />
                            ) : (
                                <Moon className="h-5 w-5" />
                            )}
                        </Button>

                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="flex items-center gap-2"
                                        data-testid="user-menu-trigger"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                            <User className="w-4 h-4 text-primary" />
                                        </div>
                                        <span className="hidden sm:inline text-sm">{user.name}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-2 py-1.5">
                                        <p className="text-sm font-medium">{user.name}</p>
                                        <p className="text-xs text-muted-foreground">{user.email}</p>
                                        {isPremium && (
                                            <span className="inline-flex items-center gap-1 mt-1 text-xs text-secondary">
                                                <Crown className="w-3 h-3" />
                                                Premium
                                            </span>
                                        )}
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild>
                                        <Link to="/settings" className="cursor-pointer" data-testid="menu-settings">
                                            <Settings className="w-4 h-4 mr-2" />
                                            Settings
                                        </Link>
                                    </DropdownMenuItem>
                                    {!isPremium && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/subscription" className="cursor-pointer text-secondary" data-testid="menu-upgrade">
                                                <Crown className="w-4 h-4 mr-2" />
                                                Upgrade to Premium
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    {isAdmin && (
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin" className="cursor-pointer text-secondary" data-testid="menu-admin">
                                                <Shield className="w-4 h-4 mr-2" />
                                                Admin Dashboard
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={logout}
                                        className="cursor-pointer text-destructive"
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Link to="/login">
                                    <Button variant="ghost" data-testid="nav-login">
                                        Login
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button className="bg-primary hover:bg-primary/90" data-testid="nav-register">
                                        Get Started
                                    </Button>
                                </Link>
                            </div>
                        )}

                        {/* Mobile Menu Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden py-4 border-t border-border">
                        {user ? (
                            <div className="flex flex-col gap-2">
                                <Link 
                                    to="/chat" 
                                    className="px-3 py-2 text-foreground hover:bg-accent rounded-lg"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Chat
                                </Link>
                                {isPremium && (
                                    <Link 
                                        to="/ustadh" 
                                        className="px-3 py-2 text-secondary hover:bg-accent rounded-lg flex items-center gap-2"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Crown className="w-4 h-4" />
                                        Ustadh Mode
                                    </Link>
                                )}
                                <Link 
                                    to="/subscription" 
                                    className="px-3 py-2 text-foreground hover:bg-accent rounded-lg"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Subscription
                                </Link>
                                {isAdmin && (
                                    <Link 
                                        to="/admin" 
                                        className="px-3 py-2 text-secondary hover:bg-accent rounded-lg flex items-center gap-2"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        <Shield className="w-4 h-4" />
                                        Admin
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <a 
                                    href="#features" 
                                    className="px-3 py-2 text-foreground hover:bg-accent rounded-lg"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Features
                                </a>
                                <a 
                                    href="#pricing" 
                                    className="px-3 py-2 text-foreground hover:bg-accent rounded-lg"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    Pricing
                                </a>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </nav>
    );
};
